import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';

import AuthLayout from '@/layout/AuthLayout/AuthLayout';
import RegistrationLayout from '@/layout/RegistrationLayout/RegistrationLayout';
import SixCatalogPage from '@/pages/SixCatalogPage/SixCatalogPage';
import { UserFormPage } from '@/pages/Register/UserFormPage';
import { EmailConfirmationPage } from '@/pages/Register/EmailConfirmationPage';
import { PhoneConfirmationPage } from '@/pages/Register/PhoneConfirmationPage';
import { PasswordFormPage } from '@/pages/Register/PasswordFormPage';
import { RegistrationSuccessPage } from '@/pages/Register/RegistrationSuccessPage';
import ForgotPassword from '@/pages/ForgotPassword/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword/ResetPassword';
import RecoveryVerifyEmailOtpPage from '@/pages/PasswordRecovery/RecoveryVerifyEmailOtpPage';
import RecoveryVerifyPhoneOtpPage from '@/pages/PasswordRecovery/RecoveryVerifyPhoneOtpPage';
import RecoverySetPasswordPage from '@/pages/PasswordRecovery/RecoverySetPasswordPage';
import ForcedResetOtpPage from '@/pages/PasswordRecovery/ForcedResetOtpPage';
import RecoverySuccessPage from '@/pages/PasswordRecovery/RecoverySuccessPage';
import SixDetailContainer from '@/pages/SixDetail/SixDetailContainer';
import PrivacyPolicyPage from '@/pages/PrivacyPolicy/PrivacyPolicyPage';
import TermsOfServicePage from '@/pages/TermsOfService/TermsOfServicePage';
import LoginCredentials from '@/pages/Login/LoginCredentials';
import TwoFactorAuthentication from '@/pages/Login/TwoFactorAuthentication';
import { resetOrganizationState } from '@/store/slices/organization/organizationSlice';
import { store } from '@/store';
import { getMessageBrokerMultiaddrThunk } from '@/store/thunks/organization/getMessageBrokerMultiaddrThunk';
import { getMeThunk } from '@/store/thunks/user/getMeThunk';
import {
  clearRegistrationSession,
  clearAuthTokens,
  getRegistrationStep,
  getRegistrationToken,
  getUkekFromStorage,
} from '@/utils/sessionStorage';
import {
  getOrganizationIdFromToken,
  getUserIdFromToken,
  getUserTypeFromToken,
} from '@/store/utils/jwtDecoder';
import Login2FAVerification from '@/pages/Login/Login2FAVerification';
import AppLayout from '@/layout/AppLayout/AppLayout';
import {
  setUkek,
  setUserId,
  setUserType,
  restoreRegistrationState,
  resetRegistration,
} from '@/store/slices/auth/authSlice';
import { clearRegistrationData } from '@/store/slices/registration/registrationSlice';
import type { UserType } from '@/types/AuthState';
import { ensureValidSessionToken } from '@/utils/authSession';

// --- Route Definitions ---
const rootRoute = createRootRoute({
  component: Outlet,
});

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth',
  component: AuthLayout,
});

// Registration routes
const registrationLayoutRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/registration',
  component: RegistrationLayout,
  beforeLoad: () => {
    const state = store.getState().auth;
    if (state.registrationStep === undefined) {
      const savedStep = getRegistrationStep();
      const savedToken = getRegistrationToken();
      if (savedStep !== null && savedToken !== null && savedToken !== '') {
        store.dispatch(
          restoreRegistrationState({
            registrationStep: savedStep,
            registrationToken: savedToken,
          })
        );
      }
    }
    return undefined;
  },
});

const registrationIndexRoute = createRoute({
  getParentRoute: () => registrationLayoutRoute,
  path: '/',
  component: UserFormPage,
  beforeLoad: () => {
    clearRegistrationSession();
    store.dispatch(resetRegistration());
    store.dispatch(clearRegistrationData());
    return undefined;
  },
});

const registrationVerifyEmailRoute = createRoute({
  getParentRoute: () => registrationLayoutRoute,
  path: 'verify-email',
  component: EmailConfirmationPage,
  beforeLoad: () => {
    const state = store.getState().auth;
    if (
      state.registrationToken === undefined ||
      state.registrationToken === '' ||
      state.registrationStep !== 'emailCode'
    ) {
      return redirect({ to: '/registration' });
    }
    return undefined;
  },
});

const registrationVerifyPhoneRoute = createRoute({
  getParentRoute: () => registrationLayoutRoute,
  path: 'verify-phone',
  component: PhoneConfirmationPage,
  beforeLoad: () => {
    const state = store.getState().auth;
    if (state.registrationStep !== 'phoneCode') {
      return redirect({ to: '/registration' });
    }
    return undefined;
  },
});

const registrationSetPasswordRoute = createRoute({
  getParentRoute: () => registrationLayoutRoute,
  path: 'set-password',
  component: PasswordFormPage,
  beforeLoad: () => {
    const state = store.getState().auth;
    if (state.registrationStep !== 'password') {
      return redirect({ to: '/registration' });
    }
    return undefined;
  },
});

const registrationSuccessRoute = createRoute({
  getParentRoute: () => registrationLayoutRoute,
  path: 'success',
  component: RegistrationSuccessPage,
  beforeLoad: () => {
    const state = store.getState().auth;
    if (state.registrationStep !== 'complete') {
      return redirect({ to: '/registration' });
    }
    return undefined;
  },
});

// Login routes
const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/login',
  component: LoginCredentials,
  beforeLoad: async () => {
    const token = await ensureValidSessionToken();
    if (token !== null) {
      return redirect({ to: '/app' });
    }
    return undefined;
  },
});

const login2FASelectRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/login/2fa/select',
  component: TwoFactorAuthentication,
});

const login2FAVerifyRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/login/2fa/verify',
  component: Login2FAVerification,
  beforeLoad: () => {
    const authState = store.getState().auth;
    if (authState.regToken === undefined || authState.twoFactorMethod === undefined) {
      return redirect({ to: '/login' });
    }
    return undefined;
  },
});

// Password recovery routes
const forgotPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/forgot-password',
  component: ForgotPassword,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/reset-password',
  component: ResetPassword,
});

const recoverPasswordOtpRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/reset-password/otp-verify',
  component: RecoveryVerifyEmailOtpPage,
});

const forcedResetOtpRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/reset-password/forced',
  component: ForcedResetOtpPage,
  beforeLoad: () => {
    const { forgotPasswordEmailAddress } = store.getState().auth;
    if (forgotPasswordEmailAddress === undefined || forgotPasswordEmailAddress === '') {
      return redirect({ to: '/login' });
    }
    return undefined;
  },
});

const recoverPasswordTwoFactorRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/reset-password/two-factor-verify',
  component: RecoveryVerifyPhoneOtpPage,
});

const recoverSetPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/reset-password/set-password',
  component: RecoverySetPasswordPage,
});

const recoverySuccessRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/reset-password/success',
  component: RecoverySuccessPage,
});

const privacyPolicyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: PrivacyPolicyPage,
});

const termsOfServiceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: TermsOfServicePage,
});

// App layout route with beforeLoad to fetch user
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  component: AppLayout,
  beforeLoad: async ({ location }) => {
    const redirectToLogin = () =>
      redirect({
        to: '/login',
        search: { redirect: location.pathname },
      });

    const token = await ensureValidSessionToken();
    if (token == null) {
      return redirectToLogin();
    }

    // Extract userId from token
    const userId = getUserIdFromToken(token);
    if (userId == null) {
      clearAuthTokens();
      store.dispatch(resetOrganizationState());
      return redirectToLogin();
    }

    const organizationId = getOrganizationIdFromToken(token);
    if (organizationId == null) {
      clearAuthTokens();
      store.dispatch(resetOrganizationState());
      return redirectToLogin();
    }

    const rawUserType = getUserTypeFromToken(token);
    const userType: UserType | null =
      rawUserType === 'Internal' || rawUserType === 'External' ? rawUserType : null;
    store.dispatch(setUserType(userType));

    const storedUkek = getUkekFromStorage(userId);
    if (storedUkek !== null) {
      store.dispatch(setUkek(storedUkek));
      store.dispatch(setUserId(userId));
    }

    // Token exists and is valid, fetch user data
    try {
      await Promise.all([
        store.dispatch(getMeThunk(userId)).unwrap(),
        store.dispatch(getMessageBrokerMultiaddrThunk(organizationId)).unwrap(),
      ]);
    } catch (error) {
      // The JWT can still be valid while the account behind it is gone (e.g. an
      // admin deleted the user) — getMe then 404s. Clear the tokens, not just the
      // org state: otherwise /login sees a valid token and redirects straight back
      // to /app, which fails again → infinite /app⇄/login redirect loop on refresh.
      clearAuthTokens();
      store.dispatch(resetOrganizationState());
      return redirectToLogin();
    }
  },
});

const sixListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'sixes',
  component: SixCatalogPage,
});

const sixDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'sixes/$sixId',
  component: SixDetailContainer,
});

// Redirect routes
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  loader: async () => {
    const token = await ensureValidSessionToken();
    if (token !== null) {
      return redirect({ to: '/app' });
    }
    return redirect({ to: '/login' });
  },
});

const appIndexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  loader: () => redirect({ to: '/app/sixes' }),
});

const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([
    registrationLayoutRoute.addChildren([
      registrationIndexRoute,
      registrationVerifyEmailRoute,
      registrationVerifyPhoneRoute,
      registrationSetPasswordRoute,
      registrationSuccessRoute,
    ]),
    loginRoute,
    login2FASelectRoute,
    login2FAVerifyRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    recoverPasswordOtpRoute,
    forcedResetOtpRoute,
    recoverPasswordTwoFactorRoute,
    recoverSetPasswordRoute,
    recoverySuccessRoute,
  ]),
  termsOfServiceRoute,
  privacyPolicyRoute,
  indexRoute,
  appLayoutRoute.addChildren([appIndexRoute, sixListRoute, sixDetailRoute]),
]);

export const router = createRouter({
  routeTree,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
